import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, MapPin, CheckCircle2, X } from 'lucide-react';
import type { ProjectApprovalGroup, ProjectTone } from '../utils/groupApprovalsByProject';
import type { AmsAuditApproval } from '../../../services/amsAuditApprovalService';
import type { AmsProjectAudit } from '../../../services/amsProjectAuditService';
import AmsAuditApprovalRow from './AmsAuditApprovalRow';

interface Props {
  group: ProjectApprovalGroup;
  onOpenAudit: (approval: AmsAuditApproval, audit: AmsProjectAudit | null) => void;
}

const EDGE_BY_TONE: Record<ProjectTone, string> = {
  pend: 'from-amber-400 to-amber-600',
  rej: 'from-red-400 to-red-600',
  appr: 'from-emerald-400 to-emerald-600',
  default: 'from-gray-300 to-gray-400',
};

const CHIP = {
  pending: 'text-amber-700 bg-amber-50 border-amber-200',
  rejected: 'text-red-700 bg-red-50 border-red-200',
  approved: 'text-emerald-700 bg-emerald-50 border-emerald-200',
} as const;
const CHIP_DOT = { pending: 'bg-amber-500', rejected: 'bg-red-500', approved: 'bg-emerald-500' } as const;

const AmsProjectApprovalCard: React.FC<Props> = ({ group, onOpenAudit }) => {
  const { project, counts, tone, needsReview } = group;
  const code = project?.projectId || 'NEW';
  const typeLabel = project?.projectTypeId?.name || project?.projectType || '';
  const locationLabel = project?.locationId?.name || project?.location || '';

  const chips = (['pending', 'rejected', 'approved'] as const)
    .filter((k) => counts[k] > 0)
    .map((k) => (
      <span
        key={k}
        className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-[2px] text-[10.5px] font-semibold ${CHIP[k]}`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${CHIP_DOT[k]}`} />
        {counts[k]} {k}
      </span>
    ));

  const badge = needsReview ? (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-amber-200 bg-amber-50 px-2.5 py-[3px] text-[10.5px] font-bold text-amber-700">
      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
      {counts.pending} to review
    </span>
  ) : counts.rejected > 0 ? (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-red-200 bg-red-50 px-2.5 py-[3px] text-[10.5px] font-bold text-red-700">
      <X className="h-3 w-3" />
      {counts.rejected} rejected
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-[3px] text-[10.5px] font-semibold text-emerald-700">
      <CheckCircle2 className="h-3 w-3" />
      Clear
    </span>
  );

  const footer = needsReview ? (
    <span className="text-[11.5px] font-bold text-blue-700">
      {counts.pending} audit{counts.pending > 1 ? 's' : ''} awaiting you
    </span>
  ) : counts.rejected > 0 ? (
    <span className="text-[11.5px] font-semibold text-gray-400">Waiting on requester</span>
  ) : (
    <span className="text-[11.5px] font-semibold text-gray-400">Up to date</span>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
      className={`flex overflow-hidden rounded-2xl border bg-white shadow-sm transition-all hover:shadow-lg ${
        needsReview ? 'border-amber-200' : 'border-gray-200'
      } font-[family-name:var(--font-outfit)]`}
    >
      <div aria-hidden className={`w-[5px] shrink-0 bg-gradient-to-b ${EDGE_BY_TONE[tone]}`} />
      <div className="flex min-w-0 flex-1 flex-col p-4">
        <div className="flex items-start justify-between gap-2">
          <span className="rounded-md border border-blue-200 bg-blue-50 px-2 py-[3px] font-mono text-[10.5px] font-bold uppercase tracking-wider text-blue-700">
            {code}
          </span>
          {badge}
        </div>
        <h3 className="mt-2 text-[15.5px] font-bold leading-tight tracking-tight text-gray-900">
          {group.projectName}
        </h3>
        {(typeLabel || locationLabel) && (
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[12px] text-gray-500">
            {typeLabel && (
              <span className="inline-flex items-center gap-1">
                <Briefcase className="h-3 w-3 text-gray-400" />
                {typeLabel}
              </span>
            )}
            {typeLabel && locationLabel && <span className="text-gray-300">·</span>}
            {locationLabel && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3 text-gray-400" />
                {locationLabel}
              </span>
            )}
          </div>
        )}
        {chips.length > 0 && <div className="mt-3 flex flex-wrap gap-1.5">{chips}</div>}

        <div className="mt-2.5 flex flex-col border-t border-gray-100">
          {group.audits.map((item) => (
            <AmsAuditApprovalRow
              key={item.approval._id || (item.approval as any).id}
              approval={item.approval}
              audit={item.audit}
              onOpen={onOpenAudit}
            />
          ))}
        </div>

        <div className="mt-2.5 flex items-center justify-between border-t border-gray-100 pt-2.5">
          <span className="text-[11.5px] text-gray-500 tabular-nums">
            {group.audits.length} audit{group.audits.length > 1 ? 's' : ''}
          </span>
          {footer}
        </div>
      </div>
    </motion.div>
  );
};

export default AmsProjectApprovalCard;
